import React, { ReactElement } from "react";
import { Row, Input } from "antd";
import "./index.less";
import ZTypography from "@atoms/ZTypography";
import ZIcon, { Icons } from "@atoms/ZIcon";
import {
	EInputWidths,
	IInputProps,
	IPasswordProps,
	ISearchProps
} from "./interface";
import CopyToClipboard from "react-copy-to-clipboard";
import ZMessage from "../ZMessage";
import { EIcons } from "@atoms/ZIcon/interface";
import { EColors } from "@atoms/ZColors/interface";
import { useState } from "react";

const { ZText } = ZTypography;

const renderInput = (
	inputType: "normal" | "password" | "search" | "copy-contents",
	width: string | number,
	addOnAfter: "yes" | "no",
	addOnBefore: "yes" | "no",
	ref:
		| React.RefObject<HTMLInputElement>
		| ((instance: HTMLInputElement | null) => void)
		| null
		| undefined,
	className: string,
	copyText = "",
	handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
	rest: any
): ReactElement => {
	const inputWidth =
		width === "full" && (addOnAfter === "yes" || addOnBefore === "yes")
			? "full-has-addon"
			: width;

	const LeftAndRightRoundedBorders = {
		"yes-no": "rounded-none rounded-r-md",
		"no-yes": "rounded-l-md rounded-none",
		"yes-yes": "rounded-none",
		"no-no": "rounded-md"
	};
	const roundedBorderClass =
		LeftAndRightRoundedBorders[`${addOnBefore}-${addOnAfter}`];

	const commonInputClassName = `input 
	 input-width-${inputWidth} input-${addOnAfter}-addon input-${addOnBefore}-addon ${roundedBorderClass} ${className}`;
	switch (inputType) {
		case "search":
			return (
				<Input.Search
					data-cke-ignore-events="true"
					className={commonInputClassName}
					prefix={
						<ZIcon name={Icons.SEARCH} size="sm" colorVariant={7} />
					}
					ref={ref}
					allowClear
					onChange={handleChange}
					autoComplete={rest?.isAutoCompleteOff ? "off" : "on"}
					{...rest}
				/>
			);
		case "copy-contents":
			return (
				<Input
					data-cke-ignore-events="true"
					className={commonInputClassName}
					ref={ref}
					onChange={handleChange}
					autoComplete={rest?.isAutoCompleteOff ? "off" : "on"}
					{...rest}
					suffix={
						<CopyToClipboard
							text={copyText}
							onCopy={() =>
								ZMessage.success("Copied to Clipboard")
							}
						>
							<ZIcon
								name={EIcons.COPY}
								size="sm"
								fillstrokesame={true}
								color={EColors.BLUE}
								colorVariant={6}
								className="cursor-pointer"
							/>
						</CopyToClipboard>
					}
				/>
			);
		case "password":
			return (
				<Input.Password
					onChange={handleChange}
					data-cke-ignore-events="true"
					className={commonInputClassName}
					ref={ref}
					autoComplete={rest?.isAutoCompleteOff ? "off" : "on"}
					{...rest}
				/>
			);
		default:
			return (
				<Input
					data-cke-ignore-events="true"
					className={commonInputClassName}
					onChange={handleChange}
					ref={ref}
					autoComplete={rest?.isAutoCompleteOff ? "off" : "on"}
					{...rest}
				/>
			);
	}
};

const ZInput: React.FC<IInputProps | ISearchProps | IPasswordProps> = ({
	addOnAfter = "",
	addOnBefore = "",
	width = EInputWidths.lg,
	className = "",
	inputType = "normal",
	zref,
	copyText = "",
	showCount = false,
	showCountAlign = "right",
	onChange,
	...rest
}) => {
	const [inputLength, setInputLength] = useState(
		rest.value
			? Array.isArray(rest.value)
				? rest.value.join(",").length
				: rest.value.toString().length
			: 0
	);
	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setInputLength(event.target.value.length);
		onChange && onChange(event);
	};
	return (
		<div className="z-input">
			{!!addOnAfter || !!addOnBefore ? (
				<Row className={`w-full flex ${className}`}>
					{!!addOnBefore &&
						(typeof addOnBefore === "string" ? (
							<div
								className={
									"add-on-before rounded-none rounded-l-md border-0 border-t border-l border-b flex items-center"
								}
							>
								<ZText
									level={0}
									className="px-3"
									colorVariant={7}
								>
									{addOnBefore}
								</ZText>
							</div>
						) : (
							addOnBefore
						))}
					{renderInput(
						inputType,
						width,
						addOnAfter ? "yes" : "no",
						addOnBefore ? "yes" : "no",
						zref,
						(className = ""),
						copyText,
						handleChange,
						rest
					)}
					{}
					{!!addOnAfter &&
						(typeof addOnAfter === "string" ? (
							<div
								className={
									"add-on-after rounded-none rounded-r-md border-0 border-t border-r border-b flex items-center"
								}
							>
								<ZText
									level={0}
									className="px-3"
									colorVariant={7}
								>
									{addOnAfter}
								</ZText>
							</div>
						) : (
							addOnAfter
						))}
				</Row>
			) : (
				renderInput(
					inputType,
					width,
					"no",
					"no",
					zref,
					className,
					copyText,
					handleChange,
					rest
				)
			)}

			{showCount && (
				<div className={`z-input-length-count text-${showCountAlign}`}>
					{" "}
					<ZText color={EColors.GRAY} colorVariant={6}>
						{`${inputLength}${
							rest.maxLength ? `/${rest.maxLength}` : ""
						}`}{" "}
					</ZText>
				</div>
			)}
		</div>
	);
};

export { EInputWidths as InputWidths };

export default ZInput;
